import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import toast from '../components/common/toast';
import { IoMdArrowDropdown } from 'react-icons/io';
import { ChevronDown } from 'lucide-react';
import { z } from 'zod';
import { binService } from '../services/api';
import { Table, Badge, EditIcon, DeleteIcon, FormPopup, DeletePopUp, BinDeletePopUp } from '../components/common';


function getUtilizationBarClass(utilization, disabled) {
  if (disabled) return 'bg-gray-300';
  const pct = Number(utilization);
  if (pct <= 30) return 'bg-blue-700';
  if (pct <= 60) return 'bg-emerald-500';
  if (pct <= 85) return 'bg-yellow-400';
  return 'bg-pink-500';
}

function formatBinType(t) {
  if (!t) return '—';
  return t === 'HOLD' ? 'Hold' : 'Good';
}

const BIN_STATUS_OPTIONS = ['ACTIVE', 'INACTIVE'];

const getStatusVariant = (status) => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'INACTIVE':
      return 'secondary';
    default:
      return 'secondary';
  }
};

function BinStatusCell({ status, binId, onSelectNewStatus, getStatusVariant }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);

  useLayoutEffect(() => {
    if (!dropdownOpen) {
      setDropdownPosition(null);
      return;
    }
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom + 4, left: rect.left });
    }
  }, [dropdownOpen]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      const inTrigger = wrapRef.current && wrapRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inTrigger && !inDropdown) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  if (!status) return '—';
  const otherStatuses = BIN_STATUS_OPTIONS.filter((s) => s !== status);

  const dropdownMenu = dropdownOpen && otherStatuses.length > 0 && dropdownPosition && (
    createPortal(
      <div
        ref={dropdownRef}
        className="min-w-[140px] rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden"
        style={{
          position: 'fixed',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          zIndex: 10000,
        }}
      >
        <ul className="py-1 list-none" role="listbox">
          {otherStatuses.map((s) => (
            <li key={s} role="option">
              <button
                type="button"
                onClick={() => { onSelectNewStatus(s); setDropdownOpen(false); }}
                className="w-full block px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      </div>,
      document.body
    )
  );

  const handleStatusCellClick = (e) => {
    e.stopPropagation();
    setDropdownOpen((o) => !o);
  };

  return (
    <div
      className="relative inline-flex items-center gap-1 cursor-pointer"
      ref={wrapRef}
      role="button"
      tabIndex={0}
      onClick={handleStatusCellClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleStatusCellClick(e);
        }
      }}
      aria-label="Change status"
    >
      <Badge variant={getStatusVariant(status)} className="inline-flex items-center gap-1 pr-1 pointer-events-none">
        <span>{status}</span>
        <span className="p-0.5 rounded hover:bg-black/10 inline-flex">
          <IoMdArrowDropdown className="w-4 h-4" />
        </span>
      </Badge>
      {dropdownMenu}
    </div>
  );
}

export function BinManagement() {
  const [bins, setBins] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [capacityPanelOpen, setCapacityPanelOpen] = useState(true);
  const [capacityStatus, setCapacityStatus] = useState([]);
  const [formData, setFormData] = useState({
    binType: 'GOOD',
    // capacity: 0,
    location: {
      aisle: '',
      rack: '',
      shelfStart: '',
      shelfEnd: '',
    },
  });
  const [loading, setLoading] = useState(false);
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [binDeletePopUpOpen, setBinDeletePopUpOpen] = useState(false);
  const [binBatches, setBinBatches] = useState([]);
  const [availableBinsForMove, setAvailableBinsForMove] = useState([]);
  const [moveBatchesLoading, setMoveBatchesLoading] = useState(false);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [statusConfirmPayload, setStatusConfirmPayload] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [editingBinId, setEditingBinId] = useState(null);
  const [productsPopUpOpen, setProductsPopUpOpen] = useState(false);
  const [selectedBinForProducts, setSelectedBinForProducts] = useState(null);
  const [productsInBin, setProductsInBin] = useState([]);
  const [productsPopUpLoading, setProductsPopUpLoading] = useState(false);

  const openStatusConfirm = (binId, currentStatus, newStatus) => {
    setStatusConfirmPayload({ id: binId, currentStatus, newStatus });
    setStatusConfirmOpen(true);
  };

  const openProductsPopUp = async (bin) => {
    if (!bin) return;
    const binId = bin._id || bin.id;
    const binCode = bin.binCode || 'Bin';
    setSelectedBinForProducts({ binId, binCode });
    setProductsPopUpOpen(true);
    setProductsInBin([]);
    setProductsPopUpLoading(true);
    try {
      const result = await binService.getProductsForBin(binId);
      setProductsInBin(result.data || []);
    } catch (err) {
      console.error('Error fetching products for bin:', err);
      setProductsInBin([]);
    } finally {
      setProductsPopUpLoading(false);
    }
  };

  const closeProductsPopUp = () => {
    setProductsPopUpOpen(false);
    setSelectedBinForProducts(null);
    setProductsInBin([]);
  };

  const binColumns = [
    { key: 'binCode', label: 'Bin Code' },
    { key: 'binType', label: 'Type' },
    // { key: 'capacity', label: 'Capacity', align: 'right' },
    {
      key: 'location',
      label: 'Aisle',
      render: (loc) => loc?.aisle ?? '—',
    },
    {
      key: 'location',
      label: 'Rack',
      render: (loc) => loc?.rack ?? '—',
    },
    {
      key: 'location',
      label: 'Shelf Start',
      render: (loc) => loc?.shelf?.start ?? '—',
    },
    {
      key: 'location',
      label: 'Shelf End',
      render: (loc) => loc?.shelf?.end ?? '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (val, row) => (
        <BinStatusCell
          status={val}
          binId={row?._id}
          onSelectNewStatus={(newStatus) => openStatusConfirm(row?._id, val, newStatus)}
          getStatusVariant={getStatusVariant}
        />
      ),
    },
  ];

  useEffect(() => {
    fetchBins();
    fetchCapacityStatus();
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchBins();
      fetchCapacityStatus();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchBins = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const result = await binService.getBins(params);
      setBins(result.data || []);
    } catch (error) {
      console.error('Error fetching bins:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCapacityStatus = async () => {
    try {
      const params = {};
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const result = await binService.getBinCapacityStatus(params);
      setCapacityStatus(result.data || []);
    } catch (error) {
      console.error('Error fetching capacity status:', error);
    }
  };

  const toastOpt = {};

  const binSchema = z.object({
    binType: z.string().optional(),
    // capacity: z.number().positive('Capacity must be greater than 0'),
    location: z.object({
      aisle: z.string().trim().min(1, 'Aisle is required'),
      rack: z.string().trim().min(1, 'Rack is required'),
      shelfStart: z.string().optional(),
      shelfEnd: z.string().optional(),
    }),
  });

  const handleSubmit = async (valuesOrEvent) => {
    // FormPopup passes values object directly, not event
    // Check if it's an event object (has preventDefault) or values object
    let dataToSubmit;
    if (valuesOrEvent && typeof valuesOrEvent === 'object' && typeof valuesOrEvent.preventDefault === 'function') {
      // It's an event, prevent default and use formData
      valuesOrEvent.preventDefault();
      dataToSubmit = formData;
    } else {
      // It's values object from FormPopup
      dataToSubmit = valuesOrEvent || formData;
    }
    
    // Validation using Zod
    try {
      binSchema.parse(dataToSubmit);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast.error(firstError.message, toastOpt);
        return;
      }
    }
    
    const locationPayload = {
      aisle: dataToSubmit.location?.aisle || '',
      rack: dataToSubmit.location?.rack || '',
      shelf:
        dataToSubmit.location?.shelfStart != null || dataToSubmit.location?.shelfEnd != null
          ? {
              start: Number(dataToSubmit.location?.shelfStart) || 0,
              end: Number(dataToSubmit.location?.shelfEnd) || 0,
            }
          : undefined,
    };

    try {
      if (editingBinId) {
        const bin = bins.find((b) => b._id === editingBinId);
        if (!bin) {
          toast.error('Bin not found. Please refresh and try again.', toastOpt);
          return;
        }
        
        const binCode = bin.binCode || '';
        await binService.updateBin(editingBinId, {
          location: locationPayload,
          // capacity: Number(dataToSubmit.capacity) || 0,
          status: bin?.status || 'ACTIVE',
        });
        
        // Show success message - use default toast container (not popup-toast) so it persists after form closes
        toast.success(`Bin ${binCode} updated successfully`);
        setEditingBinId(null);
        
        // Reset form
        setFormData({
          binType: 'GOOD',
          // capacity: 0,
          location: {
            aisle: '',
            rack: '',
            shelfStart: '',
            shelfEnd: '',
          },
        });
        setShowForm(false);
        
        // Refresh data
        fetchBins();
        fetchCapacityStatus();
      } else {
        if (!dataToSubmit.binType) {
          toast.error('Bin type is required', toastOpt);
          return;
        }
        
        const submitData = {
          binType: dataToSubmit.binType,
          // capacity: Number(dataToSubmit.capacity) || 0,
          location: locationPayload,
        };
        
        const result = await binService.createBin(submitData);
        if (result?.error) {
          const err = new Error(result?.error || 'Failed to create bin');
          err.response = { data: result };
          throw err;
        }
        const createdBinCode = result?.data?.binCode || '';
        toast.success(`Bin ${createdBinCode} created successfully`);
        setFormData({
          binType: 'GOOD',
          // capacity: 0,
          location: {
            aisle: '',
            rack: '',
            shelfStart: '',
            shelfEnd: '',
          },
        });
        setShowForm(false);
        fetchBins();
        fetchCapacityStatus();
      }
    } catch (error) {
      console.error(editingBinId ? 'Error updating bin' : 'Error creating bin', error);
      const msg = error?.response?.data?.error || error?.message || (editingBinId ? 'Failed to update bin' : 'Failed to create bin');
      toast.error(msg, toastOpt);
      // Do not close modal or reset form on create/update error
    }
  };

  const handleEdit = (row) => {
    const bin = row;
    setFormData({
      binType: bin.binType || 'GOOD',
      // capacity: Number(bin.capacity) || 0,
      location: {
        aisle: bin.location?.aisle ?? '',
        rack: bin.location?.rack ?? '',
        shelfStart: bin.location?.shelf?.start != null ? String(bin.location.shelf.start) : '',
        shelfEnd: bin.location?.shelf?.end != null ? String(bin.location.shelf.end) : '',
      },
    });
    setEditingBinId(bin._id || bin.id);
    setShowForm(true);
  };

  const handleDelete = async (row) => {
    if (!row) {
      toast.error('No bin selected for deletion', toastOpt);
      return;
    }
    
    setRowToDelete(row);
    const id = row._id || row.id;
    
    if (!id) {
      toast.error('Invalid bin ID', toastOpt);
      return;
    }
    
    // Check if bin has batches first
    try {
      const batchesResult = await binService.getBinBatches(id);
      
      // Handle different response structures
      const batches = batchesResult?.data || batchesResult?.batches || [];
      
      if (batches && batches.length > 0) {
        // Fetch available bins (excluding current bin)
        try {
          const binsResult = await binService.getBins({ status: 'ACTIVE' });
          const availableBins = (binsResult.data || []).filter(b => String(b._id) !== String(id));
          
          setAvailableBinsForMove(availableBins);
          setBinBatches(batches);
          setBinDeletePopUpOpen(true);
        } catch (binsErr) {
          console.error('Error fetching available bins:', binsErr);
          toast.error('Error fetching available bins. Showing batches anyway.', toastOpt);
          // Still show the popup with batches even if available bins fetch fails
          setBinBatches(batches);
          setAvailableBinsForMove([]);
          setBinDeletePopUpOpen(true);
        }
      } else {
        // No batches, show normal delete popup
        setDeletePopUpOpen(true);
      }
    } catch (err) {
      console.error('Error checking batches:', err);
      const errorMsg = err?.message || 'Failed to check batches';
      toast.error(`Error: ${errorMsg}. Showing delete confirmation.`, toastOpt);
      // If error, show normal delete popup
      setDeletePopUpOpen(true);
    }
  };

  const handleMoveBatches = async (toBinId) => {
    if (!rowToDelete || !toBinId) {
      toast.error('Please select a target bin', toastOpt);
      return;
    }
    const fromBinId = rowToDelete._id || rowToDelete.id;
    
    try {
      setMoveBatchesLoading(true);
      
      // Get all batches for this bin (now includes batchId)
      const batchesResult = await binService.getBinBatches(fromBinId);
      const batches = batchesResult.data || [];
      
      if (batches.length === 0) {
        toast.error('No batches to move', toastOpt);
        setMoveBatchesLoading(false);
        return;
      }
      
      // Get target bin info for message
      const binsResult = await binService.getBins({});
      const targetBin = (binsResult.data || []).find(b => b._id === toBinId);
      const targetBinCode = targetBin?.binCode || 'selected bin';
      
      // Move each batch to the new bin
      let movedCount = 0;
      let failedCount = 0;
      const failedBatches = [];
      
      for (const batch of batches) {
        if (batch.batchId) {
          try {
            await binService.moveBatchBetweenBins({
              batchId: batch.batchId,
              fromBinId: fromBinId,
              toBinId: toBinId,
              quantity: batch.quantity,
            });
            movedCount++;
          } catch (moveErr) {
            console.error(`Error moving batch ${batch.batchNumber}:`, moveErr);
            failedCount++;
            failedBatches.push({
              batchNumber: batch.batchNumber || 'Unknown',
              error: moveErr?.message || 'Failed to move batch'
            });
          }
        } else {
          console.warn('Batch missing batchId:', batch);
          failedCount++;
          failedBatches.push({
            batchNumber: batch.batchNumber || 'Unknown',
            error: 'Batch ID missing'
          });
        }
      }
      
      // Show error if all batches failed
      if (movedCount === 0 && failedCount > 0) {
        toast.error(`Failed to move all batches. ${failedBatches[0]?.error || 'Unknown error'}`, toastOpt);
        setMoveBatchesLoading(false);
        return;
      }
      
      // Show warning if some batches failed
      if (failedCount > 0) {
        toast.warning(
          `${movedCount} batch(es) moved successfully, ${failedCount} failed. ${failedBatches[0]?.error || ''}`,
          toastOpt
        );
      }
      
      // Close popup first
      setBinDeletePopUpOpen(false);
      
      // Now delete the bin automatically only if at least some batches were moved
      if (movedCount > 0) {
        try {
          await binService.deleteBin(fromBinId);
          
          // Success message with both move and delete info
          if (failedCount === 0) {
            toast.success(
              `${movedCount} batch(es) moved to ${targetBinCode} and bin ${rowToDelete?.binCode || ''} deleted successfully`,
              toastOpt
            );
          } else {
            toast.success(
              `${movedCount} batch(es) moved to ${targetBinCode} and bin ${rowToDelete?.binCode || ''} deleted. ${failedCount} batch(es) could not be moved.`,
              toastOpt
            );
          }
        } catch (deleteErr) {
          console.error('Error deleting bin after move:', deleteErr);
          const deleteErrorMsg = deleteErr?.message || 'Unknown error';
          toast.success(
            `${movedCount} batch(es) moved to ${targetBinCode} successfully`,
            toastOpt
          );
          toast.error(`Bin could not be deleted: ${deleteErrorMsg}`, toastOpt);
        }
      }
      
      // Refresh data
      fetchBins();
      fetchCapacityStatus();
      
      // Reset state
      setBinBatches([]);
      setAvailableBinsForMove([]);
      setRowToDelete(null);
      
    } catch (err) {
      console.error('Error moving batches:', err);
      const errorMsg = err?.message || 'Failed to move batches';
      toast.error(`Error: ${errorMsg}`, toastOpt);
    } finally {
      setMoveBatchesLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) {
      toast.error('No bin selected for deletion', toastOpt);
      return;
    }
    const id = rowToDelete._id || rowToDelete.id;
    const binCode = rowToDelete?.binCode || '';
    try {
      setDeleteLoading(true);
      await binService.deleteBin(id);
      
      // Close popup and reset state
      setDeletePopUpOpen(false);
      setRowToDelete(null);
      
      // Refresh data
      fetchBins();
      fetchCapacityStatus();
      
      // Show success message
      toast.success(`Bin ${binCode} deleted successfully`, toastOpt);
    } catch (err) {
      console.error('Error deleting bin:', err);
      const errorMsg = err?.message || 'Failed to delete bin';
      
      // If bin has batches, fetch batches and show special popup
      if (err.batchesCount > 0 || errorMsg.includes('batch')) {
        try {
          const batchesResult = await binService.getBinBatches(id);
          setBinBatches(batchesResult.data || []);
          setDeletePopUpOpen(false);
          setBinDeletePopUpOpen(true);
          toast.info('This bin contains batches. Please move or delete them first.', toastOpt);
        } catch (fetchErr) {
          console.error('Error fetching batches:', fetchErr);
          toast.error(`Error: ${errorMsg}`, toastOpt);
        }
      } else {
        toast.error(`Error: ${errorMsg}`, toastOpt);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteWithBatches = async () => {
    if (!rowToDelete) {
      toast.error('No bin selected for deletion', toastOpt);
      return;
    }
    const id = rowToDelete._id || rowToDelete.id;
    try {
      setDeleteLoading(true);
      const result = await binService.deleteBin(id, true);
      
      // Close popups
      setBinDeletePopUpOpen(false);
      setDeletePopUpOpen(false);
      
      // Reset state
      setRowToDelete(null);
      setBinBatches([]);
      setAvailableBinsForMove([]);
      
      // Refresh data
      fetchBins();
      fetchCapacityStatus();
      
      // Show success message
      const message = result.batchesDeleted > 0
        ? `Bin ${rowToDelete?.binCode || ''} deleted successfully along with ${result.batchesDeleted} batch(es)`
        : `Bin ${rowToDelete?.binCode || ''} deleted successfully`;
      toast.success(message, toastOpt);
    } catch (err) {
      console.error('Error deleting bin with batches:', err);
      const errorMsg = err?.message || 'Failed to delete bin';
      toast.error(`Error: ${errorMsg}`, toastOpt);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleStatusConfirmYes = async () => {
    if (!statusConfirmPayload) return;
    const { id, newStatus } = statusConfirmPayload;
    try {
      setStatusChangeLoading(true);
      await binService.updateBinStatus(id, { status: newStatus });
      setStatusConfirmOpen(false);
      setStatusConfirmPayload(null);
      fetchBins();
      fetchCapacityStatus();
      toast.success('Bin status updated successfully', toastOpt);
    } catch (err) {
      console.error('Error updating bin status:', err);
      toast.error(err?.message || 'Failed to update status', toastOpt);
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const binActions = (row) => {
    const list = [
      {
        label: 'Edit',
        icon: EditIcon,
        onClick: handleEdit,
      },
    ];
    if (row?.status !== 'ACTIVE') {
      list.push({
        label: 'Delete',
        icon: DeleteIcon,
        onClick: handleDelete,
      });
    }
    return list;
  };

  const binFields = [
    {
      name: 'binType',
      label: 'Bin Type',
      type: 'select',
      required: true,
      options: [
        { label: 'GOOD', value: 'GOOD' },
        { label: 'HOLD', value: 'HOLD' },
      ],
      grid: '1',
    },
    // {
    //   name: 'capacity',
    //   label: 'Capacity',
    //   type: 'number',
    //   required: true,
    //   min: 1,
    //   grid: '1',
    // },
    {
      type: 'group',
      title: 'Location',
      grid: '3',
      fields: [
        {
          name: 'location.aisle',
          label: 'Aisle',
          type: 'text',
          required: true,
        },
        {
          name: 'location.rack',
          label: 'Rack',
          type: 'text',
          required: true,
        },
        {
          name: 'location.shelfStart',
          label: 'Shelf Start',
          type: 'text',
          required: true,
        },
        {
          name: 'location.shelfEnd',
          label: 'Shelf End',
          type: 'text',
          required: true,
        },
      ],
    },
  ];

  // const capacityCards = useMemo(() => {
  //   let cards = [];
    
  //   // Use API data from getBinCapacityStatus if available
  //   if (capacityStatus && capacityStatus.length > 0) {
  //     cards = capacityStatus.map((s) => ({
  //       binCode: s.binCode,
  //       type: formatBinType(s.binType),
  //       // capacity: s.capacity,
  //       current: s.currentQuantity,
  //       // available: s.availableCapacity,
  //       utilization: parseFloat(s.utilizationPercentage) || 0,
  //     }));
  //   } else if (bins && bins.length > 0) {
  //     // Fallback to bins data if capacityStatus API is empty
  //     // cards = bins.map((b) => {
  //     //   const cap = Number(b.capacity) || 0;
  //     //   const cur = Number(b.currentQuantity) || 0;
  //     //   const avail = Math.max(0, cap - cur);
  //     //   const util = cap > 0 ? (cur / cap) * 100 : 0;
  //     //   return {
  //     //     binCode: b.binCode,
  //     //     type: formatBinType(b.binType),
  //     //     capacity: cap,
  //     //     current: cur,
  //     //     available: avail,
  //     //     utilization: Math.round(util * 100) / 100,
  //     //   };
  //     // });
  //     cards = bins.map((b) => ({
  //      binCode: b.binCode,
  //      type: formatBinType(b.binType),
  //      current: Number(b.currentQuantity) || 0,
  //      utilization: 0,
  //    }));
  //   }
  //   // If both are empty, cards will be empty array (no dummy data)
    
  //   // Filter by searchTerm if provided
  //   if (searchTerm && searchTerm.trim()) {
  //     const searchLower = searchTerm.trim().toLowerCase();
  //     cards = cards.filter((card) =>
  //       card.binCode.toLowerCase().includes(searchLower)
  //     );
  //   }
    
  //   return cards;
  // }, [capacityStatus, bins, searchTerm]);


  const capacityCards = useMemo(() => {
   let cards = [];
 
   if (capacityStatus && capacityStatus.length > 0) {
     cards = capacityStatus.map((s) => ({
       binCode: s.binCode,
       type: formatBinType(s.binType),
       current: s.currentQuantity,
       utilization: parseFloat(s.utilizationPercentage) || 0,
     }));
   } else if (bins && bins.length > 0) {
     cards = bins.map((b) => ({
       binCode: b.binCode,
       type: formatBinType(b.binType),
       current: Number(b.currentQuantity) || 0,
       utilization: 0,
     }));
   }
 
   if (searchTerm && searchTerm.trim()) {
     const searchLower = searchTerm.trim().toLowerCase();
     cards = cards.filter((card) => card.binCode.toLowerCase().includes(searchLower));
   }
 
   return cards;
 }, [capacityStatus, bins, searchTerm]);
  return (
    <div
      className="page-container font-nunito"
      style={{ fontFamily: "'Nunito', sans-serif" }}
    >
      <div className="mb-6 w-full">
        <div
          className="rounded-[28px] border border-[#EEF1F5] bg-[#FFFFFF] px-6 py-4 shadow-sm"
          style={{ boxShadow: "0px 0px 4px 0px #959DA526" }}
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      
      {/* Left */}
      <div className="min-w-0">
        <h1
          style={{
            fontFamily: "Nunito",
            fontSize: 18,
            fontWeight: 600,
            color: "#161616",
            margin: 0,
          }}
          className="whitespace-nowrap"
        >
          Bin Management
        </h1>
      </div>

      {/* Right */}
      <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center md:w-auto">
        
        {/* ✅ Company style search */}
        <div className="w-full sm:w-[320px]">
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </span>

            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by Code"
              className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 text-sm text-gray-700 placeholder:text-gray-400 shadow-sm focus:border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-100"
            />
          </div>
        </div>

        {/* ✅ Company style button */}
        <button
  type="button"
  onClick={() => setShowForm(true)}
  className="h-11 shrink-0 rounded-xl px-6 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98]"
  style={{ backgroundColor: "#188742" }}
>
  Create Bin
</button>
      </div>
    </div>
        </div>
      </div>


      <section className="mt-6 mb-4 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Bin Capacity Status</h2>
          <button
            type="button"
            onClick={() => setCapacityPanelOpen((o) => !o)}
            className="grid h-9 w-9 place-items-center rounded-full text-gray-600 transition hover:bg-gray-100"
            aria-expanded={capacityPanelOpen}
          >
            <ChevronDown
              className={`h-5 w-5 transition-transform duration-200 ${capacityPanelOpen ? '' : '-rotate-180'}`}
              aria-hidden
            />
          </button>
        </div>
        <div className="mt-4 border-t border-gray-100" />
        <div
          className={`transition-all duration-200 ${capacityPanelOpen ? 'max-h-[999px] opacity-100' : 'max-h-0 overflow-hidden opacity-0'}`}
        >
          {capacityCards.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {capacityCards.map((card) => {
                const disabled = card.utilization >= 100;
                const barClass = getUtilizationBarClass(card.utilization, disabled);
                const binForCard = bins.find((b) => b.binCode === card.binCode);
                return (
                  <div
                    key={card.binCode}
                    role="button"
                    tabIndex={0}
                    onClick={() => binForCard && openProductsPopUp(binForCard)}
                    onKeyDown={(e) => e.key === 'Enter' && binForCard && openProductsPopUp(binForCard)}
                    className={
                      disabled
                        ? 'rounded-2xl border border-gray-200 bg-white p-5 opacity-50'
                        : 'rounded-2xl border border-emerald-500 bg-emerald-50/30 p-5 cursor-pointer hover:ring-2 hover:ring-emerald-200 transition-shadow'
                    }
                  >
                    <div
                      className={
                        disabled
                          ? 'rounded-xl bg-gray-200 px-4 py-2 text-center text-sm font-semibold text-gray-700'
                          : 'rounded-xl bg-emerald-300/70 px-4 py-2 text-center text-sm font-semibold text-white'
                      }
                    >
                      {card.binCode}
                    </div>
                    <div className="mt-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Type</span>
                        <span className="text-sm font-medium text-gray-900">{card.type}</span>
                      </div>
                      {/* <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Capacity</span>
                        <span className="text-sm font-medium text-gray-900">{card.capacity} Units</span>
                      </div> */}
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Current</span>
                        <span className="text-sm font-medium text-gray-900">{card.current} Units</span>
                      </div>
                      {/* <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Available</span>
                        <span className="text-sm font-medium text-gray-900">{card.available} Units</span>
                      </div> */}
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Utilization</span>
                        <span className="text-sm font-medium text-gray-900">{card.utilization}%</span>
                      </div>
                    </div>
                    <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                      <div
                        className={`h-1.5 rounded-full ${barClass}`}
                        style={{ width: `${Math.min(100, card.utilization)}%` }}
                        role="progressbar"
                        aria-valuenow={card.utilization}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-6 text-center py-8 text-gray-500">
              No bins found
            </div>
          )}
        </div>
      </section>

      <Table
        columns={binColumns}
        data={bins}
        loading={loading}
        emptyMessage="No bins found"
        actions={binActions}
        onRowClick={(row) => openProductsPopUp(row)}
      />
      <DeletePopUp
        open={deletePopUpOpen}
        onClose={() => { setDeletePopUpOpen(false); setRowToDelete(null); }}
        onConfirm={handleDeleteConfirm}
        entityLabel="bin"
      />
      <BinDeletePopUp
        open={binDeletePopUpOpen}
        onClose={() => { 
          setBinDeletePopUpOpen(false); 
          setRowToDelete(null);
          setBinBatches([]);
          setAvailableBinsForMove([]);
          // Reset will happen automatically via useEffect in BinDeletePopUp
        }}
        onDeleteWithBatches={handleDeleteWithBatches}
        onMoveBatches={handleMoveBatches}
        binCode={rowToDelete?.binCode || ''}
        batches={binBatches}
        availableBins={availableBinsForMove}
        loading={moveBatchesLoading || deleteLoading}
      />
      {productsPopUpOpen && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex: 99999 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="products-popup-title"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeProductsPopUp}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 id="products-popup-title" className="text-lg font-semibold text-gray-900">
                Products in {selectedBinForProducts?.binCode || 'Bin'}
              </h2>
              <button
                type="button"
                onClick={closeProductsPopUp}
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4 max-h-[60vh] overflow-auto">
              {productsPopUpLoading ? (
                <p className="text-sm text-gray-500 py-4">Loading...</p>
              ) : productsInBin.length === 0 ? (
                <p className="text-sm text-gray-500 py-4">No products in this bin.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-gray-600 font-medium">
                      <th className="pb-2 pr-4">Product Name</th>
                      <th className="pb-2 pr-4">Code</th>
                      <th className="pb-2 text-right">Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productsInBin.map((p, i) => (
                      <tr key={p.productId || i} className="border-b border-gray-100 last:border-0">
                        <td className="py-3 pr-4 text-gray-800">{p.productName ?? '—'}</td>
                        <td className="py-3 pr-4 text-gray-700">{p.productCode ?? '—'}</td>
                        <td className="py-3 text-right font-medium text-gray-900">{p.quantity ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={closeProductsPopUp}
                className="h-10 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {statusConfirmOpen && statusConfirmPayload && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 99999 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="bin-status-confirm-title"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => { setStatusConfirmOpen(false); setStatusConfirmPayload(null); }}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-end pr-4 pt-4">
              <button
                type="button"
                onClick={() => { setStatusConfirmOpen(false); setStatusConfirmPayload(null); }}
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 pb-2">
              <p id="bin-status-confirm-title" className="text-base text-gray-800">
                Are you sure you want to change the status from <strong>{statusConfirmPayload.currentStatus}</strong> to <strong>{statusConfirmPayload.newStatus}</strong>?
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => { setStatusConfirmOpen(false); setStatusConfirmPayload(null); }}
                className="h-10 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleStatusConfirmYes}
                disabled={statusChangeLoading}
                className="h-10 rounded-full border border-emerald-500 bg-emerald-500 px-5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                {statusChangeLoading ? 'Updating...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      <FormPopup
        open={showForm}
        title={editingBinId ? 'Edit Bin' : 'Create Bin'}
        onClose={() => {
          setShowForm(false);
          setEditingBinId(null);
          setFormData({
            binType: 'GOOD',
            // capacity: 0,
            location: { aisle: '', rack: '', shelfStart: '', shelfEnd: '' },
          });
        }}
        onSubmit={handleSubmit}
        submitText={editingBinId ? 'Update' : 'Create'}
        values={formData}
        setValues={setFormData}
        fields={binFields}
        width="xl"
      />
    </div>
  );
}
